import { addDoc, arrayUnion, collection, deleteDoc, doc, getDoc, getDocs, Timestamp, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, listAll, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "./firebase";



export const addPhotos = async ({ title, des, imgs, hidden, date }) => {
    let docUUID = "";
    try {
        const jsDate = date ? new Date(date) : new Date();
        const timestampDate = Timestamp.fromDate(jsDate);

        // Step 1: Create the document with empty urls array
        const docRef = await addDoc(collection(db, "gallery"), {
            title,
            des,
            hidden,
            date: timestampDate,
            timestamp: Timestamp.now(),
            urls: [] // initially empty
        });

        docUUID = docRef.id;

        // Step 2: Upload all images and collect URL + path
        const urlObjs = await Promise.all(
            imgs.map(async (img) => {
                const storagePath = `gallery/${docUUID}/${img.name}`;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, img);
                const url = await getDownloadURL(storageRef);
                return { url, path: storagePath };
            })
        );

        // Step 3: Update the document with image objects
        await updateDoc(docRef, { urls: urlObjs });

        console.log("Gallery document created with images:", docUUID);
        return docUUID;
    } catch (error) {
        console.error("Error adding gallery data:", error);

        // Cleanup: Delete doc if upload fails
        if (docUUID) {
            await deleteDoc(doc(db, "gallery", docUUID));
            console.warn("Firestore document deleted due to image upload failure.");
        }

        throw error;
    }
};


export const fetchPhotos = async (setData) => {
    try {
        const querySnapshot = await getDocs(collection(db, "gallery"));
        const dataArr = [];

        querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const id = docSnap.id;
            let dateStr = "";
            if (data.date?.toDate) {
                dateStr = data.date.toDate().toISOString().slice(0, 10);
            }
            dataArr.push({ id, ...data, date: dateStr,  });
        });
        setData(dataArr);
    } catch (e) {
        console.error("Firebase Error in fetchPhotos:", e);
    }
};

export const editPhotos = async ({ id, title, des, imgs, hidden, date }) => {
    try {
        const jsDate = date ? new Date(date) : new Date();
        const timestampDate = Timestamp.fromDate(jsDate);

        const docRef = doc(db, "gallery", id);

        // Update basic fields
        await updateDoc(docRef, {
            title,
            des,
            hidden,
            date: timestampDate,
            timestamp: Timestamp.now(), // optional: update last edited time
        });

        // If there are new images to upload
        if (imgs && imgs.length > 0) {
            const newURLs = await Promise.all(
                // imgs.map(async (img) => {
                //     const storageRef = ref(storage, `gallery/${id}/${img.name}`);
                //     await uploadBytes(storageRef, img);
                //     return await getDownloadURL(storageRef);
                // })
                imgs.map(async (img) => {
                    const storagePath = `gallery/${id}/${img.name}`;
                    const storageRef = ref(storage, storagePath);
                    await uploadBytes(storageRef, img);
                    const url = await getDownloadURL(storageRef);
                    return { url, path: storagePath };
                })
            );

            // Append to the existing urls array
            for (const url of newURLs) {
                await updateDoc(docRef, {
                    urls: arrayUnion(url),
                });
            }
        }

        console.log("Gallery document updated:", id);
        return id;
    } catch (error) {
        console.error("Error editing gallery data:", error);
        throw error;
    }
};

export const deleteAnImage = async (pathToDelete) => {
    try {
        // Step 1: Delete from Firebase Storage
        const storageRef = ref(storage, pathToDelete);
        await deleteObject(storageRef);
        console.log("Deleted from storage:", pathToDelete);

        // Step 2: Remove from Firestore "urls" array
        const docId = (pathToDelete.split('/'))[1]

        const docRef = doc(db, "gallery", docId);
        const snap = await getDoc(docRef);

        if (!snap.exists()) {
            console.warn(`Document ${docId} does not exist.`);
            return;
        }

        const data = snap.data();
        const currentUrls = data.urls || [];

        const updatedUrls = currentUrls.filter(imgObj => imgObj.path !== pathToDelete);

        await updateDoc(docRef, { urls: updatedUrls });

        console.log("Updated Firestore document with remaining URLs.");
    } catch (error) {
        console.error("Error deleting image:", error);
        throw error;
    }
};


export const deletePhotos = async (docId) => {
    try {
        // Step 1: Delete all files in storage/gallery/{docId}/
        const folderRef = ref(storage, `gallery/${docId}`);
        const listResult = await listAll(folderRef);

        const deletionPromises = listResult.items.map((itemRef) => deleteObject(itemRef));
        await Promise.all(deletionPromises);

        console.log(`Deleted ${listResult.items.length} image(s) from storage/gallery/${docId}/`);

        // Step 2: Delete the Firestore document
        await deleteDoc(doc(db, "gallery", docId));
        console.log(`Deleted Firestore document with ID: ${docId}`);
    } catch (error) {
        console.error("Error deleting gallery entry:", error);
        throw error;
    }
};


export const fetchHomePhotos = async (setData) => {
    try {
        const querySnapshot = await getDocs(collection(db, "home_figures"));
        const dataArr = [];

        querySnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const id = docSnap.id;
            dataArr.push({ id, ...data, });
        });
        setData(dataArr);
    } catch (e) {
        console.error("Firebase Error in fetchPhotos:", e);
    }
};


export const addHomePhotos = async ({ imgs }) => {
    let docUUID = [];
    try {
        await Promise.all(
            imgs.map(async (img) => {
                const docRef = await addDoc(collection(db, "home_figures"), {
                    timestamp: Timestamp.now(),
                    url: "",
                });
                docUUID = [...docUUID, docRef.id];
                const storagePath = `home_figures/${docUUID}`;
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, img);
                const url = await getDownloadURL(storageRef);
                await updateDoc(docRef, { url });
                return { url, path: storagePath };
            })
        );
        console.log("Figures added");
    } catch (error) {
        console.error("Error adding gallery data:", error);

        // Cleanup: Delete doc if upload fails
        if (docUUID.length > 0) {
            docUUID.forEach(async id => {
                await deleteDoc(doc(db, "gallery", id));
            })
        }
        throw error;
    }
};

export const deleteHomePhoto = async (docId) => {
    try {
        // Step 1: Delete all files in storage/gallery/{docId}/
        const folderRef = ref(storage, `home_figures/${docId}`);
        await deleteObject(folderRef)
        // const listResult = await listAll(folderRef);
        // const deletionPromises = listResult.items.map((itemRef) => deleteObject(itemRef));
        //await Promise.all(deletionPromises);

        console.log(`Deleted the image from storage/gallery/${docId}/`);

        // Step 2: Delete the Firestore document
        await deleteDoc(doc(db, "home_figures", docId));
        console.log(`Deleted Firestore document with ID: ${docId}`);
    } catch (error) {
        console.error("Error deleting gallery entry:", error);
        throw error;
    }
};